/**
 * AnglerMaterial — the anglerfish, lit almost entirely by its own lure.
 *
 * The vertex stage is vendored: world/angler/voxelAngler.js ships `ANGLER_ROD_GLSL`, which
 * integrates the illicium's arc in closed form and swings the lower jaw on its hinge. The
 * fragment stage is ours, so the animal sits under the same sky as everything else — except
 * that where it lives there is barely any sky left, which is the point.
 *
 * THREE JOBS, and they are separate:
 *   emission   the esca voxels output well past 1, so the lure is the brightest thing on
 *              screen by a wide margin even before anything else happens
 *   lighting   every other voxel is lit per-fragment from the esca's own world position with
 *              inverse-square falloff. That is what rakes the teeth and finds the jaw
 *   sky        a small amount of the game's key light, so a shark swimming past at noon in
 *              shallower water does not meet a shape cut out of the frame
 *
 * The last one is a departure from the standalone version, which had no sky at all. Here the
 * animal has to survive being looked at from above in daylight if a player wanders to the edge
 * of the drop-off at midday, and a pure silhouette would read as a hole in the seabed.
 */
import * as THREE from 'three';
import { WATER } from '../data/tuning.ts';
import { ANGLER_ROD_GLSL } from '../world/angler/voxelAngler.js';
import { LURE_GLSL, lureUniforms } from './LureLight.ts';
import { applyEnv, type SkyEnv } from '../world/DayNight.ts';

function srgb(hex: number): THREE.Color {
  return new THREE.Color().setHex(hex, THREE.SRGBColorSpace);
}

const VERT = /* glsl */ `
${ANGLER_ROD_GLSL}

uniform float uVox;

varying vec3  vWorldPos;
varying vec3  vWorldNormal;
varying vec3  vCol;
varying float vRole;
varying float vShade;
varying float vGlow;

void main() {
  vec3 centre = anglerPlace(vec3(0.0));
  vCol = aCol;
  vRole = aRole;
  vShade = aShade;
  vGlow = aGlow;

  vec4 world = modelMatrix * vec4(position * uVox + centre, 1.0);
  vWorldPos = world.xyz;
  vWorldNormal = normalize(mat3(modelMatrix) * normal);
  gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const FRAG = /* glsl */ `
uniform vec3  uSunDir;
uniform vec3  uSunColor;
uniform vec3  uAmbientTop;
uniform vec3  uAmbientBottom;
uniform vec3  uFogColor;
uniform float uFogDensity;
uniform float uPulse;
uniform float uTime;

${LURE_GLSL}

varying vec3  vWorldPos;
varying vec3  vWorldNormal;
varying vec3  vCol;
varying float vRole;
varying float vShade;
varying float vGlow;

void main() {
  vec3 N = normalize(vWorldNormal);

  if (vRole > 4.5) {
    /* The esca. Output runs well past 1 on purpose: this is the brightest thing in the frame
       and it has to survive the tone map with something left over, or the lure ends up the same
       white as a tooth. The core burns hardest and the filaments trail off. */
    vec3 c = uLureCol * (0.55 + 0.45 * vGlow) * uPulse * 2.6
           + vec3(1.0) * vGlow * uPulse * 0.9;
    gl_FragColor = vec4(c, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
    return;
  }

  vec3 base = vCol;
  if (vRole > 1.5 && vRole < 2.5) base = vec3(0.76, 0.75, 0.72);        // teeth
  else if (vRole > 2.5 && vRole < 3.5) base = vec3(0.020, 0.024, 0.030); // eye

  /* Lit by its own lure first, and by the sky only faintly. Anglerfish skin is among the least
     reflective material in nature; the whole read depends on almost nothing coming back from
     it except where the esca is pointing. */
  vec3 lit = base * vShade * lureLight(vWorldPos, N);

  float ndl = dot(N, normalize(uSunDir)) * 0.5 + 0.5;
  vec3 ambient = mix(uAmbientBottom, uAmbientTop, N.y * 0.5 + 0.5);
  lit += base * vShade * (ambient * 0.55 + uSunColor * (ndl * ndl) * 0.10);

  float fd = uFogDensity * length(cameraPosition - vWorldPos);
  vec3 col = mix(lit, uFogColor, clamp(1.0 - exp(-fd * fd), 0.0, 1.0));

  gl_FragColor = vec4(col, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export class AnglerMaterial extends THREE.ShaderMaterial {
  constructor(voxelSize: number) {
    super({
      name: 'AnglerMaterial',
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        ...lureUniforms(),
        uVox: { value: voxelSize },
        // The rod. uRodPhase is INTEGRATED on the CPU, never uTime times a live rate: scaling
        // absolute time makes the phase jump by uTime*deltaRate whenever the rate changes.
        uJaw: { value: 0 },
        uRodLen: { value: 0.62 },
        uRodArc: { value: 1.25 },
        uRodSway: { value: 1 },
        uRodPhase: { value: 0 },
        uSwim: { value: 0 },
        uSwimAmp: { value: 1 },
        uHinge: { value: new THREE.Vector3(0, -0.045, 0.145) },
        uRodRoot: { value: new THREE.Vector3(0, 0.21, 0.285) },
        uPulse: { value: 1 },
        uTime: { value: 0 },

        uSunDir: {
          value: new THREE.Vector3(WATER.sunDir[0], WATER.sunDir[1], WATER.sunDir[2]).normalize(),
        },
        uSunColor: { value: srgb(WATER.sunColor) },
        uAmbientTop: { value: srgb(WATER.ambientTop) },
        uAmbientBottom: { value: srgb(WATER.ambientBottom) },
        uFogColor: { value: srgb(WATER.fogColor) },
        uFogDensity: { value: WATER.fogDensity },
      },
      side: THREE.FrontSide,
      fog: false,
    });
  }

  /** the day cycle drives this material's lighting; see world/DayNight.ts */
  setEnv(env: SkyEnv): void {
    applyEnv(this.uniforms, env);
  }
}
